import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import TopBar from '../pages/components/topBar';
import Footer from '../pages/components/footer';
import BackToTop from '../pages/components/backToTop';
import LoginDialog from '../pages/login/dialog';
import SubscriptionsLoadingOverlay from '../pages/components/subscriptionsLoadingOverlay';
import useShareDialog from '../pages/components/shareDialog/useShareDialog.jsx';
import useSync from './useSync';
import { useEnvStore } from '../store/models/env';
import { useProfileStore } from '../store/models/profile';

function Layout() {
    const env = useEnvStore();
    const profileStore = useProfileStore();
    const navigate = useNavigate();

    const { openShareDialog, renderShareDialog } = useShareDialog();
    const { launchSync } = useSync();

    const [subscriber, setSubscriber] = useState(null);
    const [loginOpen, setLoginOpen] = useState(false);
    const [loginPublisher, setLoginPublisher] = useState(null);

    useEffect(() => {
        if (env.observed.initialized) {
            setSubscriber(env.session?.payload || null);
        }
    }, [env.observed.initialized]);

    useEffect(() => {
        if (profileStore.profileDisplay?.email) {
            setSubscriber(profileStore.profileDisplay);
        }
    }, [profileStore.observed.profile]);

    // Token expired mid-session — clear subscriber so UI drops to anonymous state
    useEffect(() => {
        if (env.observed.sessionExpired) {
            setSubscriber(null);
        }
    }, [env.observed.sessionExpired]);

    function handleSignIn() {
        setLoginPublisher(null);
        setLoginOpen(true);
    }

    function handleManage() {
        navigate('/manage');
        window.scrollTo({ top: 0 });
    }

    function handleHome() {
        navigate('/');
        window.scrollTo({ top: 0 });
    }

    function handleSync(publisher) {
        launchSync(publisher);
    }

    function handleShare(publisher) {
        openShareDialog(publisher);
    }

    function handleSignOut() {
        env.logout();
        // env.logout() calls clearAuthSession() + window.open('/', '_self')
        // which triggers a full reload — React state resets automatically
        // and the user lands on / as an anonymous visitor
    }

    function handleLoginClose() {
        setLoginOpen(false);
        setLoginPublisher(null);
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <TopBar
                subscriber={subscriber}
                onSignIn={handleSignIn}
                onManage={handleManage}
                onHome={handleHome}
            />

            <Box component="main" sx={{ flex: 1 }}>
                {/* Pages receive shared state via outlet context */}
                <Outlet context={{ subscriber, onSync: handleSync, onShare: handleShare, onSignOut: handleSignOut }} />
            </Box>

            <Footer />

            <BackToTop />

            <LoginDialog
                open={loginOpen}
                publisher={loginPublisher}
                onClose={handleLoginClose}
            />

            {renderShareDialog()}

            <SubscriptionsLoadingOverlay />
        </Box>
    );
}

export default Layout;
